/**
 * ReactPDFViewer - @react-pdf-viewer 기반 PDF 뷰어
 * DocuAI 스타일의 PDF 뷰어 (grounding 하이라이트 지원)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Worker, Viewer, SpecialZoomLevel, ScrollMode } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

// 스타일
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

// PDF.js Worker URL (CDN - CRA import.meta 호환성 문제 해결)
const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const ReactPDFViewer = ({
    file,                     // File 객체 또는 URL
    pageNumber = 1,           // 현재 페이지 (1-indexed)
    pageJumpToken = 0,        // 📄 페이지 점프 강제 트리거 토큰
    onLoadSuccess,            // PDF 로드 완료 콜백
    onPageChange,             // 페이지 변경 콜백
    highlightedField = null,  // 하이라이트할 필드명
    groundingCoordinates = null, // [x1, y1, x2, y2] 직접 좌표
    highlightText = null,     // 🔍 PDF 내 텍스트 검색 하이라이트
    pdfDimensions = null,     // { width, height } PDF 원본 크기
    onZoomChange              // 줌 변경 콜백
}) => {
    const containerRef = useRef(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [currentScale, setCurrentScale] = useState(1);
    const [pagePositions, setPagePositions] = useState([]);
    const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
    const [isDocReady, setIsDocReady] = useState(false); // 📄 문서 로드 완료 플래그

    // 플러그인 설정 (사이드바 비활성화, 툴바 유지)
    const zoomPluginInstance = zoomPlugin();
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: () => [] // 사이드바 비활성화, 툴바(돋보기 등)는 유지
    });

    // 📄 [Page Navigation] jumpToPage API 추출
    const { toolbarPluginInstance } = defaultLayoutPluginInstance;
    const { pageNavigationPluginInstance } = toolbarPluginInstance;
    const { jumpToPage } = pageNavigationPluginInstance;

    // File 객체 → URL 변환
    useEffect(() => {
        if (!file) {
            setFileUrl(null);
            return;
        }

        if (file instanceof File) {
            const url = URL.createObjectURL(file);
            setFileUrl(url);
            return () => URL.revokeObjectURL(url);
        } else if (typeof file === 'string') {
            setFileUrl(file);
        }
    }, [file]);

    // 스크롤 이벤트 핸들러
    const handleScroll = useCallback(() => {
        const scrollContainer = document.querySelector('[data-testid="core__inner-pages"]');
        if (scrollContainer) {
            setScrollPosition({
                x: scrollContainer.scrollLeft,
                y: scrollContainer.scrollTop
            });
        }
    }, []);

    // 페이지 위치 업데이트
    const updatePagePositions = useCallback(() => {
        const positions = [];
        const scrollContainer = document.querySelector('[data-testid="core__inner-pages"]');

        if (!scrollContainer) return;

        // 모든 페이지 요소 찾기
        let pageIndex = 0;
        while (true) {
            const pageElement = document.querySelector(`[data-testid="core__page-layer-${pageIndex}"]`);
            if (!pageElement) break;

            const containerRect = scrollContainer.getBoundingClientRect();
            const pageRect = pageElement.getBoundingClientRect();

            positions.push({
                top: pageRect.top - containerRect.top + scrollContainer.scrollTop,
                left: pageRect.left - containerRect.left + scrollContainer.scrollLeft,
                width: pageRect.width,
                height: pageRect.height
            });
            pageIndex++;
        }

        setPagePositions(positions);
    }, []);

    // 📄 [Page Navigation] pageNumber prop 변경 시 해당 페이지로 점프
    // pageJumpToken이 변경될 때마다 항상 실행 (같은 페이지로도 재점프)
    useEffect(() => {
        if (!pageNumber || pageNumber < 1 || !isDocReady) return;

        const pageIndex = pageNumber - 1; // jumpToPage는 0-indexed

        // 약간의 딜레이 후 점프 (렌더링 완료 대기)
        const timer = setTimeout(() => {
            try {
                jumpToPage(pageIndex);
                console.log(`📄 [ReactPDFViewer] jumpToPage(${pageIndex}) → 페이지 ${pageNumber}로 이동`);
            } catch (e) {
                console.warn(`⚠️ [ReactPDFViewer] jumpToPage 실패:`, e);
            }
        }, 50);

        return () => clearTimeout(timer);
    }, [pageJumpToken, isDocReady, jumpToPage]);

    // 🔍 [Text Search Highlight] PDF.js 텍스트 레이어에서 직접 텍스트 찾아 하이라이트
    const lastHighlightRef = useRef(null);

    // 기존 하이라이트 제거
    const clearTextHighlights = useCallback(() => {
        const overlays = document.querySelectorAll('.rpv-text-highlight-overlay');
        overlays.forEach(el => el.remove());
    }, []);

    // 텍스트 레이어에서 텍스트 검색 + 라인 하이라이트
    const highlightTextInPdf = useCallback((searchText) => {
        if (!searchText) return 0;
        const searchStr = String(searchText).trim();
        if (searchStr === '') return 0;

        const normalizedSearch = searchStr.toLowerCase();
        let matchCount = 0;
        let firstMatchSpan = null; // 🔍 첫 번째 매칭 span 추적 (스크롤용)

        // 현재 페이지의 텍스트 레이어 찾기
        const currentPageIndex = pageNumber - 1;
        const pageLayer = document.querySelector(`[data-testid="core__page-layer-${currentPageIndex}"]`);
        if (!pageLayer) {
            console.warn('🔍 [TextHighlight] 페이지 레이어 없음:', currentPageIndex);
            return 0;
        }

        // 텍스트 레이어 내 모든 span 검색
        const textLayer = pageLayer.querySelector('.rpv-core__text-layer');
        if (!textLayer) {
            console.warn('🔍 [TextHighlight] 텍스트 레이어 없음');
            return 0;
        }

        // pageLayer의 position 확인 (오버레이 absolute 배치 기준)
        const pageLayerStyle = window.getComputedStyle(pageLayer);
        if (pageLayerStyle.position === 'static') {
            pageLayer.style.position = 'relative';
        }

        const pageLayerRect = pageLayer.getBoundingClientRect();
        const textSpans = textLayer.querySelectorAll('span');
        const matchedLines = new Map(); // Y좌표별 라인 정보

        textSpans.forEach(span => {
            const spanText = span.textContent?.toLowerCase() || '';
            if (spanText.length >= 2 && spanText.includes(normalizedSearch)) {
                matchCount++;
                if (!firstMatchSpan) firstMatchSpan = span; // 첫 번째 매칭 span 저장
                // span의 위치를 pageLayer 기준으로 계산
                const spanRect = span.getBoundingClientRect();
                const relativeTop = Math.round(spanRect.top - pageLayerRect.top);
                // 같은 Y좌표 라인은 하나로 합침
                if (!matchedLines.has(relativeTop)) {
                    matchedLines.set(relativeTop, {
                        top: relativeTop,
                        height: spanRect.height
                    });
                }
            }
        });

        // 📌 pageLayer에 오버레이 삽입 (텍스트 레이어 바깥 → mix-blend-mode 영향 없음)
        matchedLines.forEach(pos => {
            const overlay = document.createElement('div');
            overlay.className = 'rpv-text-highlight-overlay';
            overlay.style.cssText = `
                position: absolute;
                left: 0;
                top: ${pos.top}px;
                width: 100%;
                height: ${pos.height + 2}px;
                background-color: #D6FFBB;
                pointer-events: none;
                z-index: 1;
                mix-blend-mode: multiply;
            `;
            pageLayer.appendChild(overlay);
        });

        console.log(`🔍 [TextHighlight] "${searchStr}" → ${matchCount}개 매칭, ${matchedLines.size}줄 하이라이트`);

        // 🔍 첫 번째 매칭 텍스트 span으로 스크롤 (줌 시 화면 밖에 있을 수 있음)
        // 오버레이(absolute)가 아닌 실제 텍스트 span(DOM flow 내)에 scrollIntoView 호출
        if (firstMatchSpan) {
            try {
                // PDF 뷰어의 스크롤 컨테이너 찾기
                const scrollContainer = pageLayer.closest('.rpv-core__inner-pages')
                    || pageLayer.closest('[data-testid="core__inner-pages"]')
                    || pageLayer.closest('[style*="overflow"]');

                if (scrollContainer) {
                    // 스크롤 컨테이너 기준으로 span 위치 계산
                    const spanRect = firstMatchSpan.getBoundingClientRect();
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const isVisible = spanRect.top >= containerRect.top && spanRect.bottom <= containerRect.bottom;

                    if (!isVisible) {
                        // span이 보이지 않으면 스크롤
                        const scrollOffset = spanRect.top - containerRect.top - (containerRect.height / 2);
                        scrollContainer.scrollTop += scrollOffset;
                        console.log(`🔍 [TextHighlight] 스크롤: ${Math.round(scrollOffset)}px (컨테이너 내 위치 조정)`);
                    }
                } else {
                    // 폴백: 직접 scrollIntoView
                    firstMatchSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            } catch (e) {
                console.warn('🔍 [TextHighlight] 스크롤 실패:', e);
            }
        }

        return matchCount;
    }, [pageNumber]);

    useEffect(() => {
        if (!isDocReady) return;

        // 이전 하이라이트 항상 제거
        clearTextHighlights();

        const searchStr = highlightText != null ? String(highlightText).trim() : '';
        if (searchStr === '') {
            lastHighlightRef.current = null;
            return;
        }

        // 같은 텍스트 + 같은 페이지 반복 방지
        const highlightKey = `${searchStr}_${pageNumber}`;
        if (lastHighlightRef.current === highlightKey) return;
        lastHighlightRef.current = highlightKey;

        // 딜레이 후 하이라이트 (페이지 이동/렌더링 완료 대기)
        const timer = setTimeout(() => {
            const count = highlightTextInPdf(searchStr);
            console.log(`🔍 [TextHighlight] "${searchStr}" → ${count}개 매칭 (페이지 ${pageNumber})`);
        }, 300);

        return () => clearTimeout(timer);
    }, [highlightText, pageNumber, isDocReady, highlightTextInPdf, clearTextHighlights]);

    // 문서 로드 완료 핸들러
    const handleDocumentLoad = (e) => {
        const { numPages } = e.doc;
        setIsDocReady(true); // 📄 문서 로드 완료
        const scrollContainer = document.querySelector('[data-testid="core__inner-pages"]');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        }

        // 페이지 위치 업데이트
        setTimeout(updatePagePositions, 500);

        if (onLoadSuccess) {
            onLoadSuccess({ numPages });
        }

        console.log(`📄 [ReactPDFViewer] PDF 로드 완료: ${numPages} 페이지`);
    };

    // 페이지 변경 핸들러
    const handlePageChange = (e) => {
        if (onPageChange) {
            onPageChange(e.currentPage + 1); // 1-indexed로 변환
        }
    };

    // 줌 변경 핸들러
    const handleZoomChange = (e) => {
        setCurrentScale(e.scale);
        updatePagePositions();
        if (onZoomChange) {
            onZoomChange(e.scale);
        }
    };

    // Grounding 하이라이트 렌더링
    const renderGroundingHighlight = () => {
        if (!groundingCoordinates || groundingCoordinates.length !== 4) return null;

        const [x1, y1, x2, y2] = groundingCoordinates;

        // 페이지 요소 직접 찾기
        const currentPageIndex = pageNumber - 1;
        const pageElement = document.querySelector(`[data-testid="core__page-layer-${currentPageIndex}"]`);
        if (!pageElement) return null;

        const pageRect = pageElement.getBoundingClientRect();
        const containerElement = containerRef.current;
        if (!containerElement) return null;
        const containerRect = containerElement.getBoundingClientRect();

        // PDF 좌표 → 화면 좌표 변환
        let scaleX = 1;
        let scaleY = 1;

        if (pdfDimensions && pdfDimensions.width && pdfDimensions.height) {
            scaleX = pageRect.width / pdfDimensions.width;
            scaleY = pageRect.height / pdfDimensions.height;
        }

        const scaledX = x1 * scaleX;
        const scaledY = y1 * scaleY;
        const scaledWidth = (x2 - x1) * scaleX;
        const scaledHeight = (y2 - y1) * scaleY;

        // 컨테이너 기준 상대 좌표
        const finalLeft = pageRect.left - containerRect.left + scaledX;
        const finalTop = pageRect.top - containerRect.top + scaledY;

        console.log(`🎯 [Grounding] 좌표 변환:`, {
            original: { x1, y1, x2, y2 },
            pdfDimensions,
            pageSize: { width: pageRect.width, height: pageRect.height },
            scale: { scaleX, scaleY },
            scaled: { scaledX, scaledY, scaledWidth, scaledHeight },
            final: { finalLeft, finalTop }
        });

        return (
            <div
                style={{
                    position: 'absolute',
                    left: finalLeft,
                    top: finalTop,
                    width: Math.max(scaledWidth, 30),
                    height: Math.max(scaledHeight, 15),
                    backgroundColor: 'rgba(255, 215, 0, 0.35)', // 반투명 노란색만
                    borderRadius: '2px',
                    pointerEvents: 'none',
                    zIndex: 9999
                }}
            />
        );
    };

    if (!fileUrl) {
        return (
            <div className="flex items-center justify-center h-full bg-gray-100">
                <p className="text-gray-500">PDF 파일을 선택해주세요</p>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="h-full relative" style={{ minHeight: '400px' }}>
            {/* @react-pdf-viewer */}
            <Worker workerUrl={PDFJS_WORKER_URL}>
                <Viewer
                    fileUrl={fileUrl}
                    plugins={[defaultLayoutPluginInstance, zoomPluginInstance]}
                    defaultScale={SpecialZoomLevel.PageWidth}
                    scrollMode={ScrollMode.Vertical}
                    onDocumentLoad={handleDocumentLoad}
                    onPageChange={handlePageChange}
                    onZoom={handleZoomChange}
                    renderError={(error) => (
                        <div className="text-center p-5 bg-red-50 text-red-500">
                            <h3 className="font-bold">PDF 로드 오류</h3>
                            <p>{error.message}</p>
                        </div>
                    )}
                />
            </Worker>

            {/* Grounding 하이라이트 오버레이 */}
            {renderGroundingHighlight()}
        </div>
    );
};

export default ReactPDFViewer;
